import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../../../api/authService';
import { toast } from 'react-hot-toast';
import { extractErrorMessage } from '../../../utils/errorHandler';

const AutoLoginRedirect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const performAutoLogin = async () => {
      try {
        setLoading(true);
        
        // Check if user is already logged in
        if (authService.isAuthenticated()) {
          // User is already logged in, redirect directly
          toast.success('Already logged in. Redirecting...');
          setTimeout(() => {
            navigate('/agent/tickets/new', { replace: true });
          }, 500);
          return;
        }
        
        // Extract email, password, and phone from query parameters and decode them
        const email = decodeURIComponent(searchParams.get('email') || '');
        const password = decodeURIComponent(searchParams.get('pass') || '');
        const phone = decodeURIComponent(searchParams.get('phone_number') || '');
        const call_id = decodeURIComponent(searchParams.get('call_id') || '');

        // Validate parameters
        if (!email || !password) {
          setError('Email and password are required')
          toast.error('Email and password are required');
          setTimeout(() => {
            navigate('/');
          }, 2000);
          return;
        }

        // Perform login
        try {
          const response = await authService.login(email, password);
          
          if (response?.token) {
            // Login successful
            toast.success('Login successful! Redirecting...');
            
            // Wait a moment for token to be stored
            setTimeout(() => {
              // Redirect to /agent/tickets/new with phone number in state
              navigate('/agent/tickets/new', { 
                replace: true,
                state: { phoneNumber: phone || null }
              });
            }, 500);
          } else {
            // Login failed
            setError('Login failed. Invalid credentials.');
            toast.error('Login failed. Invalid credentials.');
            setTimeout(() => {
              navigate('/signin');
            }, 2000);
          }
        } catch (loginError) {
          // Handle login error
          const errorMessage = extractErrorMessage(loginError, 'Login failed. Please try again.');
          setError(errorMessage);
          toast.error(errorMessage);
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
      } catch (err) {
        setError('An error occurred during auto-login');
        toast.error('An error occurred during auto-login');
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    performAutoLogin();
  }, [navigate, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Logging in...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="text-red-600 dark:text-red-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-900 dark:text-gray-100 text-lg font-semibold mb-2">Login Failed</p>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
          <p className="text-gray-500 dark:text-gray-500 text-sm mt-4">Redirecting to sign in page...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default AutoLoginRedirect;

